export const MANUAL_GATEWAY_TEMPLATE_VARIABLES = [
  'amount',
  'currency',
  'invoice_id',
  'customer_email',
  'id',
  'email',
  'price',
  'price_usd',
  'product_name',
  'quantity',
] as const;

export type ManualGatewayTemplateVariable = typeof MANUAL_GATEWAY_TEMPLATE_VARIABLES[number];
export type ManualGatewayTemplateVars = Record<string, string>;

export const MANUAL_GATEWAY_TEMPLATE_VARIABLE_EXAMPLES = [
  '{{amount}}',
  '{{currency}}',
  '{{invoice_id}}',
  '{{customer_email}}',
  '{id}',
  '{email}',
  '{price}',
  '{currency}',
  '{price_usd}',
  '{product_name}',
  '{quantity}',
] as const;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildVariablePattern(key: string, style: 'double' | 'single'): RegExp {
  const escapedKey = escapeRegExp(key);
  if (style === 'double') {
    return new RegExp(`\\{\\{\\s*${escapedKey}\\s*\\}\\}`, 'gi');
  }

  return new RegExp(`(?<!\\{)\\{\\s*${escapedKey}\\s*\\}(?!\\})`, 'gi');
}

export function renderManualGatewayTemplate(
  template: string,
  vars: ManualGatewayTemplateVars,
  options: { encodeValues?: boolean } = {},
): string {
  let rendered = template;
  for (const [key, value] of Object.entries(vars)) {
    const replacement = options.encodeValues ? encodeURIComponent(value) : value;
    rendered = rendered.replace(buildVariablePattern(key, 'double'), replacement);
    rendered = rendered.replace(buildVariablePattern(key, 'single'), replacement);
  }
  return rendered;
}

const PLACEHOLDER_SCAN_PATTERNS = [
  /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g,
  /(?<!\{)\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}(?!\})/g,
] as const;

const REMAINING_PLACEHOLDER_PATTERNS = [
  /\{\{[\s\S]*?\}\}/g,
  /(?<!\{)\{[^{}]+\}(?!\})/g,
] as const;

export const MANUAL_GATEWAY_CUSTOM_FIELD_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/;

export function isValidManualGatewayCustomFieldName(name: string): boolean {
  return MANUAL_GATEWAY_CUSTOM_FIELD_NAME_PATTERN.test(name.trim());
}

export function findUnsupportedManualGatewayPlaceholders(
  template: string,
  additionalVariables: Iterable<string> = [],
): string[] {
  if (!template.trim()) {
    return [];
  }

  const allowedVariables = new Set<string>(MANUAL_GATEWAY_TEMPLATE_VARIABLES);
  for (const variable of additionalVariables) {
    const normalized = variable.trim().toLowerCase();
    if (normalized) {
      allowedVariables.add(normalized);
    }
  }

  const unsupported = new Set<string>();
  // Track the raw spans of syntactically-valid placeholders that ARE allowed, so
  // we don't double-flag them when sweeping for malformed syntax below.
  const allowedRawSpans = new Set<string>();
  for (const pattern of PLACEHOLDER_SCAN_PATTERNS) {
    for (const match of template.matchAll(pattern)) {
      const raw = match[0];
      const name = match[1]?.toLowerCase();
      if (name && !allowedVariables.has(name)) {
        unsupported.add(raw);
      } else if (name) {
        allowedRawSpans.add(raw);
      }
    }
  }

  // Also reject ANY remaining {{...}}/{...} placeholder syntax that is not an
  // allowed variable — a mistyped or filtered token such as `{{order-id}}` or
  // `{{ amount | money }}` is not caught by the strict name patterns above, so
  // it would survive save and only fail at checkout (session 400 / raw token in
  // instructions). Flag it at save time instead.
  for (const pattern of REMAINING_PLACEHOLDER_PATTERNS) {
    for (const match of template.matchAll(pattern)) {
      const raw = match[0];
      if (!allowedRawSpans.has(raw)) {
        unsupported.add(raw);
      }
    }
  }

  return [...unsupported];
}

export function collectUnsupportedManualGatewayPlaceholders(
  ...templates: Array<string | null | undefined>
): string[] {
  const unsupported = new Set<string>();
  for (const template of templates) {
    for (const placeholder of findUnsupportedManualGatewayPlaceholders(template ?? '')) {
      unsupported.add(placeholder);
    }
  }
  return [...unsupported];
}

export function collectUnsupportedManualGatewayPlaceholdersWithExtras(
  additionalVariables: Iterable<string>,
  ...templates: Array<string | null | undefined>
): string[] {
  const unsupported = new Set<string>();
  for (const template of templates) {
    for (const placeholder of findUnsupportedManualGatewayPlaceholders(template ?? '', additionalVariables)) {
      unsupported.add(placeholder);
    }
  }
  return [...unsupported];
}

const RESERVED_MANUAL_GATEWAY_FIELD_NAMES = new Set<string>(
  MANUAL_GATEWAY_TEMPLATE_VARIABLES.map((name) => name.toLowerCase()),
);

export function isReservedManualGatewayFieldName(name: string): boolean {
  const normalized = name.trim().toLowerCase();
  return normalized.length > 0 && RESERVED_MANUAL_GATEWAY_FIELD_NAMES.has(normalized);
}

export function findRemainingManualGatewayPlaceholders(template: string): string[] {
  if (!template.trim()) {
    return [];
  }

  const remaining = new Set<string>();
  for (const pattern of REMAINING_PLACEHOLDER_PATTERNS) {
    for (const match of template.matchAll(pattern)) {
      remaining.add(match[0]);
    }
  }

  return [...remaining];
}

export function stableGatewayFieldValuesKey(fields: Record<string, string> | null | undefined): string {
  if (!fields) {
    return '{}';
  }

  const sortedEntries = Object.keys(fields)
    .sort((left, right) => left.localeCompare(right))
    .map((key) => [key, fields[key]] as const);

  return JSON.stringify(sortedEntries);
}

export function isSafeManualGatewayRedirectUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) {
    return false;
  }

  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export function mergeManualGatewayTemplateVars(
  trustedVars: ManualGatewayTemplateVars,
  gatewayFieldVars: Record<string, string> = {},
): ManualGatewayTemplateVars {
  return {
    ...gatewayFieldVars,
    ...trustedVars,
  };
}
